import React from 'react';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import { compose } from 'redux';
import { composeModules } from 'holocron';
import { addErrorToReport, loadLanguagePack } from '@americanexpress/one-app-ducks';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { THEMES } from '@americanexpress/consumer-travel-base';
import { Surface } from '@americanexpress/dls-react';
import {
  MODULE_NAME, DATE_PICKER_MODULE_NAME,
  LOCATIONS_MODULE_NAME, ROOMS_TRAVELERS_MODULE_NAME, SEARCH_REDIRECT_MODULE_NAME,
} from '../constants/globalConstants';
import reducer from '../ducks';
import TravelSearchHotels from './TravelSearchHotels';
import ThemeContext from '../context/themeContext';

const TravelSearchHotelsWrapper = ({
  id,
  locale,
  languageData,
  theme,
  isMosaicDesign,
  shouldDisableMosaicFullscreenMobile,
  minNights,
  maxAdults,
  maxChildren,
  maxRooms,
  taxonomy,
  searchRedirectConfig,
  moduleLoadStatus,
  route: {
    theme: routeTheme,
  },
}) => {
  const isBlueTheme = routeTheme === THEMES.THEME_BLUE
    || (routeTheme === undefined && theme === THEMES.THEME_BLUE);

  const surfaceVariant = isBlueTheme ? 'fg-brand' : 'foreground';
  const surfaceClassNames = isBlueTheme ? '' : 'radius-container-b';

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <ThemeContext.Provider value={theme}>
        <Surface variant={surfaceVariant} className={surfaceClassNames} data-testid="surface-wrapper">
          <TravelSearchHotels
            id={id}
            isMosaicDesign={isMosaicDesign}
            minNights={minNights}
            maxAdults={maxAdults}
            maxChildren={maxChildren}
            maxRooms={maxRooms}
            taxonomy={taxonomy}
            searchRedirectConfig={searchRedirectConfig}
            moduleLoadStatus={moduleLoadStatus}
            shouldDisableMosaicFullscreenMobile={shouldDisableMosaicFullscreenMobile}
          />
        </Surface>
      </ThemeContext.Provider>
    </IntlProvider>
  );
};

export const loadModuleData = async ({ store: { dispatch } }) => {
  try {
    await Promise.allSettled([
      dispatch(loadLanguagePack(MODULE_NAME, { fallbackLocale: 'en-US' })),
      dispatch(composeModules([
        { name: SEARCH_REDIRECT_MODULE_NAME },
        { name: LOCATIONS_MODULE_NAME },
        { name: ROOMS_TRAVELERS_MODULE_NAME },
        { name: DATE_PICKER_MODULE_NAME },
      ])),
    ]);
  } catch (e) {
    await dispatch(addErrorToReport(e, {
      collectionMethod: 'loadModuleData',
      moduleID: MODULE_NAME,
    }));
    throw e;
  }
};

TravelSearchHotelsWrapper.propTypes = {
  id: PropTypes.string,
  locale: PropTypes.string.isRequired,
  languageData: PropTypes.shape({}).isRequired,
  theme: PropTypes.oneOf(
    [THEMES.THEME_BLUE, THEMES.THEME_WHITE]
  ),
  isMosaicDesign: PropTypes.bool,
  shouldDisableMosaicFullscreenMobile: PropTypes.bool,
  minNights: PropTypes.number,
  maxAdults: PropTypes.number,
  maxChildren: PropTypes.number,
  maxRooms: PropTypes.number,
  taxonomy: PropTypes.string,
  searchRedirectConfig: PropTypes.shape({}),
  moduleLoadStatus: PropTypes.string.isRequired,
  route: PropTypes.shape({
    theme: PropTypes.oneOf([THEMES.THEME_BLUE, THEMES.THEME_WHITE]),
  }),
};

TravelSearchHotelsWrapper.defaultProps = {
  id: MODULE_NAME,
  isMosaicDesign: false,
  shouldDisableMosaicFullscreenMobile: false,
  minNights: null,
  maxAdults: null,
  maxChildren: null,
  maxRooms: null,
  taxonomy: undefined,
  theme: THEMES.THEME_BLUE,
  searchRedirectConfig: {},
  route: {
    theme: undefined,
  },
};

const hocChain = compose(
  oneAppModuleWrapper(MODULE_NAME)
);

TravelSearchHotelsWrapper.holocron = {
  name: MODULE_NAME,
  loadModuleData,
  reducer,
};

export default hocChain(TravelSearchHotelsWrapper);
